/**
 * A class that represents a single group of state.
 * @class
 */ class $06c1141a5fa11022$var$AppState {
    /**
   * @param {*} value - The initial value of this state
   * @param {EventTarget} [eventTarget] - The EventTarget to use for firing state changes
   */ constructor(value, eventTarget){
        /** @type {EventTarget} */ this.eventTarget = eventTarget || new EventTarget();
        /** @type {*} */ this.value = value;
    }
    /**
   * Set the value and fire `change` event.
   * @param {*} newValue - The new value
   */ setValue(newValue) {
        this.value = newValue;
        this.eventTarget.dispatchEvent(new CustomEvent("change", {
            detail: {
                source: this
            }
        }));
    }
}
var $06c1141a5fa11022$export$2e2bcd8739ae039 = $06c1141a5fa11022$var$AppState;


/**
 * @typedef {import("../IController").IController} IController
 * @typedef {import("../../Models/AppState").default} AppState
 */ /**
 * A controller that just passes the value of `event.target`.
 * `sourceElement` is not needed as we are using `event.target` anyway.
 * @class
 * @implements {IController}
 */ class $005cdf1a540a6299$var$EventTargetPassingController {
    /**
   * @param {AppState} targetState
   */ constructor(targetState){
        /** @type {AppState} */ this.targetState = targetState;
        /**
     * The update callback.
     * @type {function(Event): void}
     */ this.update = (event)=>{
            if (!event.target) return;
            // @ts-ignore
            this.targetState.setValue(event.target.value);
        };
    }
}
var $005cdf1a540a6299$export$2e2bcd8739ae039 = $005cdf1a540a6299$var$EventTargetPassingController;


/**
 * The intermediate state during RLE parsing.
 * @class
 */ class $7eb306c3bbaced74$var$PatternParser {
    /**
   * Initialize the parser with the given state.
   *
   * @param {Object} initialState - The initial state to override with
   * @property {boolean|undefined} initialState.isFinished
   * @property {Array<Cell>|undefined} initialState.cells
   * @property {Array<number>|undefined} initialState.runCount
   * @property {Cell|undefined} initialState.currentCell
   *
   */ constructor(initialState = {}){
        /** @type {boolean} */ this.isFinished = initialState.isFinished || false;
        /** @type {Array<Cell>} */ this.cells = initialState.cells || [];
        /** @type {number} */ this.runCount = initialState.runCount || 0;
        /** @type {Cell} */ this.currentCell = initialState.currentCell || [
            0,
            0
        ];
    }
    finishParsing() {
        this.isFinished = true;
    }
    addNewlines() {
        const [, /* x */ y] = this.currentCell;
        const runCount = this.runCount || 1;
        this.currentCell = [
            0,
            y + runCount
        ];
        this.runCount = 0;
    }
    /**
   * Append `digit` to `this.runCount`
   * @param {number} digit - The digit to append
   */ updateRunCount(digit) {
        this.runCount = this.runCount * 10 + digit;
    }
    /**
   * @param {number} cellState
   */ drawRun(cellState) {
        const [x, y] = this.currentCell;
        const runCount = this.runCount || 1;
        const newCells = new Array(runCount).fill(0).map((v, i)=>[
                x + i,
                y
            ]);
        // Refactor addNewCells into a dependency if needs variation in behavior
        const addNewCells = (nc, s)=>{
            if (s) this.cells = this.cells.concat(nc);
        };
        addNewCells(newCells, cellState);
        this.currentCell = [
            x + runCount,
            y
        ];
        this.runCount = 0;
    }
}
var $7eb306c3bbaced74$export$2e2bcd8739ae039 = $7eb306c3bbaced74$var$PatternParser;


/**
 * @typedef {import('../Neighbors/INTNeighbors').INTNeighbor} INTNeighbor
 */ /**
 * An Isotropic non-totalistic rule with the given birth and survival conditions.
 */ class $5fea755afec9d72f$var$INTRule {
    /**
   * @param {Array<INTNeighbor>} births - The birth conditions
   * @param {Array<INTNeighbor>} survivals - The survival conditions
   */ constructor(births, survivals){
        /** @type {Array<INTNeighbor>} */ this.births = births;
        /** @type {Array<INTNeighbor>} */ this.survivals = survivals;
    }
}
var $5fea755afec9d72f$export$2e2bcd8739ae039 = $5fea755afec9d72f$var$INTRule;


/**
 * A Totalistic Rule subclass with the given birth and survival conditions.
 */ class $6c574ea9d853ec19$var$TotalisticRule {
    /**
   * @param {Array<number>} births
   * @param {Array<number>} survivals
   */ constructor(births, survivals){
        /** @type {Array<number>} */ this.births = births;
        /** @type {Array<number>} */ this.survivals = survivals;
    }
}
var $6c574ea9d853ec19$export$2e2bcd8739ae039 = $6c574ea9d853ec19$var$TotalisticRule;


const $7e08f398c2dbe9e4$export$9e91b46a83237852 = (ruleString)=>{
    /**
   * @private
   * @typedef {{
   *  success: boolean,
   *  births: Array<number>,
   *  survivals: Array<number>,
   *  isBirth: boolean,
   * }} State
   */ /**
   * @param {State} state
   * @param {string} char
   * @property {1} char.length
   * @returns {State} The updated state
   */ const updateState = (state, char)=>{
        if (!state.success) return state;
        const { success: success , births: births , survivals: survivals , isBirth: isBirth  } = state;
        switch(char){
            case "0":
            case "1":
            case "2":
            case "3":
            case "4":
            case "5":
            case "6":
            case "7":
            case "8":
                (isBirth ? births : survivals).push(parseInt(char, 10));
                return {
                    success: success,
                    births: births,
                    survivals: survivals,
                    isBirth: isBirth
                };
            case "b":
            case "B":
                return {
                    success: success,
                    births: births,
                    survivals: survivals,
                    isBirth: true
                };
            case "s":
            case "S":
                return {
                    success: success,
                    births: births,
                    survivals: survivals,
                    isBirth: false
                };
            case "/":
                return {
                    success: success,
                    births: births,
                    survivals: survivals,
                    isBirth: !isBirth
                };
            default:
                return {
                    success: false,
                    births: [],
                    survivals: [],
                    isBirth: false
                };
        }
    };
    const finalState = Array.from(ruleString).reduce(updateState, {
        success: true,
        births: [],
        survivals: [],
        isBirth: false
    });
    if (!finalState.success) return undefined;
    const births = [
        ...new Set(finalState.births).keys()
    ];
    const survivals = [
        ...new Set(finalState.survivals).keys()
    ];
    return new (0, $6c574ea9d853ec19$export$2e2bcd8739ae039)(births, survivals);
};
const $7e08f398c2dbe9e4$export$6f3ef6963fbc63d1 = (ruleString)=>{
    /**
   * @typedef {import('../../BaseTypes/Neighbors/INTNeighbors.js').INTNeighbor} INTNeighbor
   */ /**
   * @private
   * @typedef {{
   *  isBirth: boolean,
   *  births: Set<INTNeighbor>,
   *  survivals: Set<INTNeighbor>,
   * }} State
   */ // Dependent function and objects
    /** @type {function(State): State} */ const copyWithBirth = (state)=>({
            isBirth: true,
            births: state.births,
            survivals: state.survivals
        });
    /** @type {function(State): State} */ const copyWithSurvival = (state)=>({
            isBirth: false,
            births: state.births,
            survivals: state.survivals
        });
    /** @type {function(State): State} */ const copyWithToggledBirth = (state)=>({
            isBirth: !state.isBirth,
            births: state.births,
            survivals: state.survivals
        });
    // Assumes word matches `/[0-8][-cekainyqjrtwz]/`
    /** @type {Array<Array<INTNeighbor>>} */ const allNeighborsByCount = [
        [
            "0"
        ],
        [
            "1c",
            "1e"
        ],
        [
            "2c",
            "2e",
            "2a",
            "2k",
            "2i",
            "2n"
        ],
        [
            "3c",
            "3e",
            "3a",
            "3k",
            "3i",
            "3n",
            "3j",
            "3q",
            "3r",
            "3y"
        ],
        [
            "4c",
            "4e",
            "4a",
            "4k",
            "4i",
            "4n",
            "4j",
            "4q",
            "4r",
            "4y",
            "4t",
            "4w",
            "4z"
        ],
        [
            "5c",
            "5e",
            "5a",
            "5k",
            "5i",
            "5n",
            "5j",
            "5q",
            "5r",
            "5y"
        ],
        [
            "6c",
            "6e",
            "6a",
            "6k",
            "6i",
            "6n"
        ],
        [
            "7c",
            "7e"
        ],
        [
            "8"
        ]
    ];
    /** @type {Array<(Map<string, INTNeighbor>)>} */ const neighborsByCountAndChar = [
        new Map(),
        new Map([
            [
                "c",
                "1c"
            ],
            [
                "e",
                "1e"
            ]
        ]),
        new Map([
            [
                "c",
                "2c"
            ],
            [
                "e",
                "2e"
            ],
            [
                "a",
                "2a"
            ],
            [
                "k",
                "2k"
            ],
            [
                "i",
                "2i"
            ],
            [
                "n",
                "2n"
            ]
        ]),
        new Map([
            [
                "c",
                "3c"
            ],
            [
                "e",
                "3e"
            ],
            [
                "a",
                "3a"
            ],
            [
                "k",
                "3k"
            ],
            [
                "i",
                "3i"
            ],
            [
                "n",
                "3n"
            ],
            [
                "j",
                "3j"
            ],
            [
                "q",
                "3q"
            ],
            [
                "r",
                "3r"
            ],
            [
                "y",
                "3y"
            ]
        ]),
        new Map([
            [
                "c",
                "4c"
            ],
            [
                "e",
                "4e"
            ],
            [
                "a",
                "4a"
            ],
            [
                "k",
                "4k"
            ],
            [
                "i",
                "4i"
            ],
            [
                "n",
                "4n"
            ],
            [
                "j",
                "4j"
            ],
            [
                "q",
                "4q"
            ],
            [
                "r",
                "4r"
            ],
            [
                "y",
                "4y"
            ],
            [
                "w",
                "4w"
            ],
            [
                "t",
                "4t"
            ],
            [
                "z",
                "4z"
            ]
        ]),
        new Map([
            [
                "c",
                "5c"
            ],
            [
                "e",
                "5e"
            ],
            [
                "a",
                "5a"
            ],
            [
                "k",
                "5k"
            ],
            [
                "i",
                "5i"
            ],
            [
                "n",
                "5n"
            ],
            [
                "j",
                "5j"
            ],
            [
                "q",
                "5q"
            ],
            [
                "r",
                "5r"
            ],
            [
                "y",
                "5y"
            ]
        ]),
        new Map([
            [
                "c",
                "6c"
            ],
            [
                "e",
                "6e"
            ],
            [
                "a",
                "6a"
            ],
            [
                "k",
                "6k"
            ],
            [
                "i",
                "6i"
            ],
            [
                "n",
                "6n"
            ]
        ]),
        new Map([
            [
                "c",
                "7c"
            ],
            [
                "e",
                "7e"
            ]
        ]),
        new Map()
    ];
    /** @type {function(string): Set<INTNeighbor>} */ const getNeighborsFromWord = (word)=>{
        const neighborSet = new Set();
        const count = parseInt(word[0], 10);
        // The chars before the first `-` are births
        // The chars after them are survivals
        const [birthsString, ...survivalsStrings] = word.slice(1).split("-");
        if (birthsString === "") allNeighborsByCount[count].forEach((n)=>neighborSet.add(n));
        else Array.from(birthsString).forEach((c)=>neighborSet.add(neighborsByCountAndChar[count].get(c)));
        Array.from(survivalsStrings.join("")).forEach((c)=>neighborSet.delete(neighborsByCountAndChar[count].get(c)));
        return neighborSet;
    };
    // Main logic
    /** @type {State} */ const initialState = {
        isBirth: false,
        births: new Set(),
        survivals: new Set()
    };
    const words = ruleString.match(/[bBsS/]|[0-8][-cekainyqjrtwz]*/g);
    if (words.reduce((psum, w)=>psum + w.length, 0) !== ruleString.length) return undefined;
    const finalState = words.reduce((state, word)=>{
        if (word === "b" || word === "B") return copyWithBirth(state);
        if (word === "s" || word === "S") return copyWithSurvival(state);
        if (word === "/") return copyWithToggledBirth(state);
        // Neighbors
        const neighborsSet = getNeighborsFromWord(word);
        const targetSet = state.isBirth ? state.births : state.survivals;
        neighborsSet.forEach((n)=>targetSet.add(n));
        return state;
    }, initialState);
    return new (0, $5fea755afec9d72f$export$2e2bcd8739ae039)([
        ...finalState.births
    ], [
        ...finalState.survivals
    ]);
};


/** @module */ const $c0a50bf5651c6c70$var$mapStateFromChar = new Map([
    [
        ".",
        0
    ],
    [
        "A",
        1
    ],
    [
        "B",
        0
    ],
    [
        "C",
        1
    ],
    [
        "D",
        0
    ],
    [
        "E",
        1
    ],
    [
        "F",
        0
    ],
    [
        "b",
        0
    ],
    [
        "o",
        1
    ]
]);
/**
 * @private
 * @param {PatternParser} parserState
 * @param {string} c
 * @returns {PatternParser}
 */ const $c0a50bf5651c6c70$var$updateParserState = (parserState, c)=>{
    // Passthrough if the parser is finished.
    if (parserState.isFinished) return parserState;
    if (c === "!") {
        parserState.finishParsing();
        return parserState;
    }
    // Update the run count if it is a digit
    if (c >= "0" && c <= "9") {
        const d = c.charCodeAt(0) - "0".charCodeAt(0);
        parserState.updateRunCount(d);
        return parserState;
    }
    // Jump to next line if it is '$'
    if (c === "$") {
        parserState.addNewlines();
        return parserState;
    }
    // Draw the run if it is a character
    const currState = $c0a50bf5651c6c70$var$mapStateFromChar.get(c) || 0;
    parserState.drawRun(currState);
    return parserState;
};
const $c0a50bf5651c6c70$export$3b629cd0ff482602 = (rleBodyString)=>{
    const parseResult = [
        ...rleBodyString
    ].reduce($c0a50bf5651c6c70$var$updateParserState, new (0, $7eb306c3bbaced74$export$2e2bcd8739ae039)());
    parseResult.finishParsing(); // No-op for now but semantically needed
    return parseResult.cells;
};
/* Body extracting related code */ /**
 * @private
 * @param {string} rleString
 * @returns {{body: string, rule: string|null}} The RLE Body and Rule, both as a string
 */ const $c0a50bf5651c6c70$var$extractParts = (rleString)=>{
    const lines = rleString.split("\n").map((line)=>line.replace(/\s/g, "")).filter((line)=>line !== ""); // We ignore empty lines
    /** @type {function(string): boolean} */ const isNotComment = (line)=>!line.startsWith("#");
    const firstNonCommentIndex = lines.findIndex(isNotComment);
    const headerRegex = /^x=\d+,y=\d+,rule=(.*)/;
    const matchedHeader = headerRegex.exec(lines[firstNonCommentIndex]);
    const bodyStartIndex = firstNonCommentIndex + (matchedHeader !== null ? 1 : 0);
    const body = lines.slice(bodyStartIndex).join("");
    const rule = matchedHeader ? matchedHeader[1] : null;
    return {
        body: body,
        rule: rule
    };
};
const $c0a50bf5651c6c70$export$98e6a39c04603d36 = (rleString)=>{
    const { rule: ruleString , body: body  } = $c0a50bf5651c6c70$var$extractParts(rleString);
    const pattern = $c0a50bf5651c6c70$export$3b629cd0ff482602(body);
    const rule = (0, $7e08f398c2dbe9e4$export$9e91b46a83237852)(ruleString) || (0, $7e08f398c2dbe9e4$export$6f3ef6963fbc63d1)(ruleString) || undefined;
    return {
        pattern: pattern,
        rule: rule
    };
};


/**
 * A bounding box consists of min/max of the X/Y coordinates.
 * For an empty pattern the bounding box is `(Inf, Inf, -Inf, -Inf)`
 * because it's an identity in the addition below.
 */ class $4fe0a788fb08d993$var$BoundingBox {
    /**
   * @param {number} xmin
   * @param {number} xmax
   * @param {number} ymin
   * @param {number} ymax
   */ constructor(xmin = Infinity, xmax = -Infinity, ymin = Infinity, ymax = -Infinity){
        /** @type {number} */ this.xmin = xmin;
        /** @type {number} */ this.xmax = xmax;
        /** @type {number} */ this.ymin = ymin;
        /** @type {number} */ this.ymax = ymax;
    }
    /**
   * Return a new bounding box that is the result of
   * merging `this` with the `other` bounding box.
   *
   * @param {BoundingBox} other - The other box to merge with.
   * @returns {BoundingBox} - The merged bounding box.
   */ plus(other) {
        const xmin = Math.min(this.xmin, other.xmin);
        const xmax = Math.max(this.xmax, other.xmax);
        const ymin = Math.min(this.ymin, other.ymin);
        const ymax = Math.max(this.ymax, other.ymax);
        return new $4fe0a788fb08d993$var$BoundingBox(xmin, xmax, ymin, ymax);
    }
    /**
   * Given an array of bounding boxes, merge all of them and return the merged box.
   *
   * @param  {Array<BoundingBox>} boxes - The boxes to merge.
   * @returns {BoundingBox} - The merged bounding box.
   */ static sum(boxes) {
        return boxes.reduce((psum, box)=>psum.plus(box), new $4fe0a788fb08d993$var$BoundingBox());
    }
}
var $4fe0a788fb08d993$export$2e2bcd8739ae039 = $4fe0a788fb08d993$var$BoundingBox;


/**
 * @private
 * @type {function(number, number): string}
 */ const $b455d5ebeb71c1de$var$cellToString = (x, y)=>`${x} ${y}`;
/**
 * @private
 * @type {function(string): Cell}
 */ const $b455d5ebeb71c1de$var$stringToCell = (s)=>s.split(" ").map((e)=>parseInt(e, 10));
/**
 * Map data structure that accepts Cells: [x: number, y: number] as keys.
 *
 * We need to implement this because
 * JS does not support non-primitive value types as keys (as of ES6)
 */ class $b455d5ebeb71c1de$var$CellMap {
    /**
   * @param {Array<CellEntry>} [entries=[]]
   */ constructor(entries = []){
        /** @type {Map<string, *>} */ this.map = new Map();
        entries.forEach(([x, y], value)=>this.set([
                x,
                y
            ], value));
    }
    /**
   * Generate and return a `CellMap` from list of keys.
   *
   * @param {Array} keys - Array of cells, each of type `[number, number]`.
   * @returns - The CellMap having the given keys with values mapped to null
   */ static fromKeys(keys) {
        const map = new $b455d5ebeb71c1de$var$CellMap();
        keys.forEach(([x, y])=>map.set([
                x,
                y
            ], null));
        return map;
    }
    /**
   * Generate and return a `CellMap` with the given entries.
   *
   * This is the same as the constructor, but is presented here
   * as an analogue to `fromKeys`.
   *
   * @param {Array} entries - Array of [cell, value] pairs
   * @returns The resulting CellMap having the given entries
   */ static fromEntries(entries) {
        return new $b455d5ebeb71c1de$var$CellMap(entries);
    }
    /**
   * Return whether a cell is in the map to Map.
   *
   * @param {*} key - cell to search for, has type `[number, number]`.
   * @returns A boolean indicating whether the cell is in the set
   */ has(key) {
        const [x, y] = key;
        return this.map.has($b455d5ebeb71c1de$var$cellToString(x, y));
    }
    /**
   * Get the value for the given key.
   * If not found, return `defaultValue` as a fallback.
   *
   * @param {Cell} key - The key to get values for.
   * @param {*} defaultValue - The value to return when the key is not found.
   * @returns - The found value or `defaultValue`
   */ get(key, defaultValue) {
        const [x, y] = key;
        const value = this.map.get($b455d5ebeb71c1de$var$cellToString(x, y));
        return value !== undefined ? value : defaultValue;
    }
    /**
   * Set the value for the given key.
   *
   * @param {Cell} key - The key to set values for.
   * @param {*} value - The value to set.
   */ set(key, value) {
        const [x, y] = key;
        this.map.set($b455d5ebeb71c1de$var$cellToString(x, y), value);
    }
    /**
   * Return the number of keys of the map, analogous to `Map`.
   */ get size() {
        return this.map.size;
    }
    /**
   * Return the keys of this map as in `Map`.
   * @returns A list of keys for the map.
   */ keys() {
        return [
            ...this.map.keys()
        ].map($b455d5ebeb71c1de$var$stringToCell);
    }
    /**
   * Return the entries (i.e. key, value pairs) of this map, as in `Map`.
   * @returns A list of entries for the map.
   */ entries() {
        return [
            ...this.map.entries()
        ].map(([s, v])=>[
                $b455d5ebeb71c1de$var$stringToCell(s),
                v
            ]);
    }
}
var $b455d5ebeb71c1de$export$2e2bcd8739ae039 = $b455d5ebeb71c1de$var$CellMap;


const $33c87e009b185634$export$decf60f46c2b52d8 = (board, maxGens = 1000)=>{
    /**
   * @param {Array} array
   * @param {Set} set
   * @returns {boolean}
   */ const haveSameMembers = (array, set)=>array.length === set.size && array.every((cell)=>set.has(cell));
    const appendNextBoard = ({ result: result , lastBoards: lastBoards , initialCellsSet: initialCellsSet  })=>{
        if (result.length !== 0) return {
            result: result,
            lastBoards: lastBoards,
            initialCellsSet: initialCellsSet
        };
        /** @type {IBoard} */ const lastBoard = lastBoards[lastBoards.length - 1];
        /** @type {IBoard} */ const currBoard = lastBoard.after();
        if (haveSameMembers(currBoard.getCells(), initialCellsSet)) return {
            result: lastBoards,
            lastBoards: [],
            initialCellsSet: initialCellsSet
        };
        return {
            result: [],
            lastBoards: lastBoards.concat(currBoard),
            initialCellsSet: initialCellsSet
        };
    };
    const repeatForMaxGens = new Array(maxGens).fill();
    const initialData = {
        result: [],
        lastBoards: [
            board
        ],
        initialCellsSet: (0, $b455d5ebeb71c1de$export$2e2bcd8739ae039).fromKeys(board.getCells())
    };
    const phases = repeatForMaxGens.reduce(appendNextBoard, initialData).result;
    return phases; // Return empty array on failure
};
const $33c87e009b185634$export$2f3e39639c1a2e58 = (oscPhaseBoards)=>{
    // Get the list of oscillator phases.
    // Return a list of `{ cell, aliveGens }` where `aliveGens` is the gens `cell` was alive.
    const getAliveGensByCell = (phases)=>{
        const phasesSet = phases.map((0, $b455d5ebeb71c1de$export$2e2bcd8739ae039).fromKeys);
        const allCells = (0, $b455d5ebeb71c1de$export$2e2bcd8739ae039).fromKeys([].concat(...phases)).keys();
        const gens = phases.map((_, gen)=>gen);
        return allCells.map((cell)=>({
                cell: cell,
                aliveGens: gens.filter((gen)=>phasesSet[gen].has(cell))
            }));
    };
    // Get the list of gens a cell was alive.
    // Return the subperiod.
    const getSubperiodOfOneCell = (aliveGens, period)=>{
        const aliveGensSet = new Set(aliveGens);
        const subperiods = new Array(period).fill().map((_, i)=>i + 1).filter((n)=>period % n === 0);
        const isSubperiodValid = (n)=>aliveGens.map((g)=>(g + n) % period).every((g)=>aliveGensSet.has(g));
        const validSubperiods = subperiods.filter(isSubperiodValid);
        return Math.min(...validSubperiods);
    };
    const period = oscPhaseBoards.length;
    const result = getAliveGensByCell(oscPhaseBoards.map((b)=>b.getCells())).map(({ cell: cell , aliveGens: aliveGens  })=>({
            cell: cell,
            subperiod: getSubperiodOfOneCell(aliveGens, period)
        }));
    return result;
};
const $33c87e009b185634$export$a50fceef87b2948 = (board)=>{
    // Basic functions
    /** @type {function(Array<number>): number} */ const getAverage = (l)=>l.reduce((a, b)=>a + b, 0) / l.length;
    /** @type {function(number): string} */ const formatFloat = (f)=>f.toFixed(2);
    /** @type {function(number): string} */ const formatPercentage = (f)=>`${(100 * f).toFixed(2)}%`;
    // Status functions
    /** @typedef {Array<{cell: Cell, subperiod: number}>} Subperiods */ /** @type {function(Subperiods): number} */ const getRotorCount = (subperiods)=>subperiods.filter(({ subperiod: subperiod  })=>subperiod !== 1).length;
    /** @type {function(Subperiods, number): number} */ const getStrictRotorCount = (subperiods, period)=>subperiods.filter(({ subperiod: subperiod  })=>subperiod === period).length;
    /** @type {function(Subperiods): number} */ const getVolatility = (subperiods)=>getRotorCount(subperiods) / subperiods.length;
    /** @type {function(Subperiods, number): number} */ const getStrictVolatility = (subperiods, period)=>getStrictRotorCount(subperiods, period) / subperiods.length;
    // Main code
    const phaseBoards = $33c87e009b185634$export$decf60f46c2b52d8(board);
    const period = phaseBoards.length;
    if (period === 0) return {
        success: false,
        message: "Failed to detect period of pattern"
    };
    const populations = phaseBoards.map((b)=>b.getPop());
    const subperiods = $33c87e009b185634$export$2f3e39639c1a2e58(phaseBoards);
    const result = {
        success: true,
        pattern: board.getCells(),
        period: period,
        phases: phaseBoards.map((p)=>p.getCells()),
        subperiods: subperiods,
        minPop: Math.min(...populations),
        maxPop: Math.max(...populations),
        avgPop: formatFloat(getAverage(populations)),
        numCells: subperiods.length,
        numRotorCells: getRotorCount(subperiods),
        numStatorCells: subperiods.length - getRotorCount(subperiods),
        numStrictRotorCells: getStrictRotorCount(subperiods, period),
        volatility: formatPercentage(getVolatility(subperiods)),
        strictVolatility: formatPercentage(getStrictVolatility(subperiods, period)),
        boundingBox: (0, $4fe0a788fb08d993$export$2e2bcd8739ae039).sum(phaseBoards.map((p)=>p.getBox()))
    };
    return result;
};



/**
 * @typedef {import('../../../BaseTypes/Rule/Rule').Rule} Rule
 * @typedef {import('../IBoard').IBoard} IBoard
 * @typedef {import('./SimpleTotalisticBoard').default} SimpleTotalisticBoard
 */ /**
 * @typedef {function(Array<Cell>): Array<Cell>} TransitionFunction
 */ /**
 * This simple interface consists of a `pattern` and a `transitionFunction`.
 * It applies the `transitionFunction` on the `pattern` to get the next generation.
 * Other auxillary information is extracted from the `pattern`.
 *
 * IMPORTANT:
 *
 *  This class is made to reduce boilerplate.
 *  You don't need to inherit from this class;
 *  You just have to implement `I{Totalistic,INT}Board` interfaces.
 *
 *  If you are using this class, make sure to initialize `this.transitionFunction` properly,
 *  with the rule given as the parameter, with the proper type of {@link TransitionFunction}.
 *  For example classes, see {@link SimpleTotalisticBoard}.
 *
 * @implements {IBoard}
 *
 */ class $7c60ee827e62c391$var$AbcSimpleBoard {
    /**
   * @param {TwoStatePattern} pattern
   * @param {Rule} rule
   * @param {number} gen
   */ constructor(pattern, rule, gen){
        /** @type {TwoStatePattern} */ this.pattern = pattern;
        /** @type {Rule} */ this.rule = rule;
        /** @type {number} */ this.gen = gen;
        /**
     * This should be overriden by concrete implementations!
     * @type {any}
     */ this.transitionFunction = undefined;
    }
    getCells() {
        return this.pattern;
    }
    getCellsAndStates() {
        return this.pattern.map((cell)=>[
                cell,
                1
            ]);
    }
    getBox() {
        return (0, $4fe0a788fb08d993$export$2e2bcd8739ae039).sum(this.pattern.map(([x, y])=>new (0, $4fe0a788fb08d993$export$2e2bcd8739ae039)(x, x, y, y)));
    }
    getPop() {
        return this.pattern.length;
    }
    /**
   * Return a new board with the pattern iterated by the given amount.
   *
   * @param {number} [gens=1] - Number of generations to iterate
   * @returns {AbcSimpleBoard} - The new board
   */ after(gens = 1) {
        const repeatForGens = new Array(gens).fill();
        /** @type {TransitionFunction} */ const transFunc = this.transitionFunction;
        /** @type {Array<Cell>} */ const iteratedPattern = repeatForGens.reduce(transFunc, this.pattern);
        /** @type {any} */ const ThisClass = this.constructor;
        return new ThisClass(iteratedPattern, this.rule, this.gen + gens);
    }
}
var $7c60ee827e62c391$export$2e2bcd8739ae039 = $7c60ee827e62c391$var$AbcSimpleBoard;



const $cdbcd7a52f96e34a$var$moore = [
    [
        -1,
        -1
    ],
    [
        -1,
        0
    ],
    [
        -1,
        1
    ],
    [
        0,
        -1
    ],
    [
        0,
        0
    ],
    [
        0,
        1
    ],
    [
        1,
        -1
    ],
    [
        1,
        0
    ],
    [
        1,
        1
    ]
];
/**
 * @private
 * @param {Array<Cell>} cellsArray
 * @param {Array<Cell>} neighbors
 * @returns {CellMap}
 */ const $cdbcd7a52f96e34a$var$getNeighborCounts = (cellsArray, neighbors)=>{
    const neighborCountsMap = new (0, $b455d5ebeb71c1de$export$2e2bcd8739ae039)();
    cellsArray.forEach(([x, y])=>{
        neighbors.forEach(([dx, dy])=>{
            const targetCell = [
                x + dx,
                y + dy
            ];
            const count = neighborCountsMap.get(targetCell) || 0;
            neighborCountsMap.set(targetCell, count + 1);
        });
    });
    return neighborCountsMap;
};
/**
 * @typedef {import('./AbcSimpleBoard').TransitionFunction} TransitionFunction
 */ /**
 * @private
 * @param {TotalisticRule} totalisticRule
 * @returns {TransitionFunction}
 */ const $cdbcd7a52f96e34a$var$makeTransFromTotalisticRule = (totalisticRule)=>(cellsArray)=>{
        const { births: births , survivals: survivals  } = totalisticRule;
        const neighborCounts = $cdbcd7a52f96e34a$var$getNeighborCounts(cellsArray, $cdbcd7a52f96e34a$var$moore);
        const cellsSet = (0, $b455d5ebeb71c1de$export$2e2bcd8739ae039).fromKeys(cellsArray);
        const ruleCondition = ([cell, count])=>cellsSet.has(cell) ? survivals.includes(count - 1) // -1: `count` includes `cell`, while B/S notation doesn't
             : births.includes(count);
        return [
            ...neighborCounts.entries()
        ].filter(ruleCondition).map(([cell /* count */ ])=>cell);
    };
/**
 * A sample implementation for totalistic rules.
 * @class
 * @implements {ITotalisticBoard}
 */ class $cdbcd7a52f96e34a$var$SimpleTotalisticBoard extends (0, $7c60ee827e62c391$export$2e2bcd8739ae039) {
    /**
   * Initialize the board with the given pattern and rule.
   *
   * @constructor
   * @param {TwoStatePattern} pattern - The initial pattern.
   * @param {TotalisticRule} rule - The rule to operate on the pattern with.
   * @param {number} [gen=0] - The initial generation.
   */ constructor(pattern, rule, gen = 0){
        super(pattern, rule, gen);
        this.transitionFunction = $cdbcd7a52f96e34a$var$makeTransFromTotalisticRule(rule);
    }
}
var $cdbcd7a52f96e34a$export$2e2bcd8739ae039 = $cdbcd7a52f96e34a$var$SimpleTotalisticBoard;






/** @module */ /**
 * An enum for specifying neighbors for INT rules.
 */ const $a97e00b3534022fc$export$160e8bdd97bfce3a = {
    // eslint-disable-next-line quote-props
    0: "0",
    "1c": "1c",
    "1e": "1e",
    "2c": "2c",
    "2e": "2e",
    "2a": "2a",
    "2k": "2k",
    "2i": "2i",
    "2n": "2n",
    "3c": "3c",
    "3e": "3e",
    "3a": "3a",
    "3k": "3k",
    "3i": "3i",
    "3n": "3n",
    "3j": "3j",
    "3q": "3q",
    "3r": "3r",
    "3y": "3y",
    "4c": "4c",
    "4e": "4e",
    "4a": "4a",
    "4k": "4k",
    "4i": "4i",
    "4n": "4n",
    "4j": "4j",
    "4q": "4q",
    "4r": "4r",
    "4y": "4y",
    "4t": "4t",
    "4w": "4w",
    "4z": "4z",
    "5c": "5c",
    "5e": "5e",
    "5a": "5a",
    "5k": "5k",
    "5i": "5i",
    "5n": "5n",
    "5j": "5j",
    "5q": "5q",
    "5r": "5r",
    "5y": "5y",
    "6c": "6c",
    "6e": "6e",
    "6a": "6a",
    "6k": "6k",
    "6i": "6i",
    "6n": "6n",
    "7c": "7c",
    "7e": "7e",
    // eslint-disable-next-line quote-props
    8: "8"
};
const $a97e00b3534022fc$export$10cb2c208909f427 = `
    0 1c 1e 2a 1c 2c 2a 3i 1e 2a 2e 3a 2k 3n 3j 4a
    0 1c 1e 2a 1c 2c 2a 3i 1e 2a 2e 3a 2k 3n 3j 4a
    1e 2k 2e 3j 2a 3n 3a 4a 2i 3r 3e 4r 3r 4i 4r 5i
    1e 2k 2e 3j 2a 3n 3a 4a 2i 3r 3e 4r 3r 4i 4r 5i
    1c 2c 2k 3n 2n 3c 3q 4n 2a 3i 3j 4a 3q 4n 4w 5a
    1c 2c 2k 3n 2n 3c 3q 4n 2a 3i 3j 4a 3q 4n 4w 5a
    2k 3y 3k 4k 3q 4y 4q 5j 3r 4t 4j 5n 4z 5r 5q 6a
    2k 3y 3k 4k 3q 4y 4q 5j 3r 4t 4j 5n 4z 5r 5q 6a
    1e 2k 2i 3r 2k 3y 3r 4t 2e 3j 3e 4r 3k 4k 4j 5n
    1e 2k 2i 3r 2k 3y 3r 4t 2e 3j 3e 4r 3k 4k 4j 5n
    2e 3k 3e 4j 3j 4k 4r 5n 3e 4j 4e 5c 4j 5y 5c 6c
    2e 3k 3e 4j 3j 4k 4r 5n 3e 4j 4e 5c 4j 5y 5c 6c
    2a 3n 3r 4i 3q 4y 4z 5r 3a 4a 4r 5i 4q 5j 5q 6a
    2a 3n 3r 4i 3q 4y 4z 5r 3a 4a 4r 5i 4q 5j 5q 6a
    3j 4k 4j 5y 4w 5k 5q 6k 4r 5n 5c 6c 5q 6k 6n 7c
    3j 4k 4j 5y 4w 5k 5q 6k 4r 5n 5c 6c 5q 6k 6n 7c
    1c 2n 2k 3q 2c 3c 3n 4n 2k 3q 3k 4q 3y 4y 4k 5j
    1c 2n 2k 3q 2c 3c 3n 4n 2k 3q 3k 4q 3y 4y 4k 5j
    2a 3q 3j 4w 3i 4n 4a 5a 3r 4z 4j 5q 4t 5r 5n 6a
    2a 3q 3j 4w 3i 4n 4a 5a 3r 4z 4j 5q 4t 5r 5n 6a
    2c 3c 3y 4y 3c 4c 4y 5e 3n 4n 4k 5j 4y 5e 5k 6e
    2c 3c 3y 4y 3c 4c 4y 5e 3n 4n 4k 5j 4y 5e 5k 6e
    3n 4y 4k 5k 4n 5e 5j 6e 4i 5r 5y 6k 5r 6i 6k 7e
    3n 4y 4k 5k 4n 5e 5j 6e 4i 5r 5y 6k 5r 6i 6k 7e
    2a 3q 3r 4z 3n 4y 4i 5r 3j 4w 4j 5q 4k 5k 5y 6k
    2a 3q 3r 4z 3n 4y 4i 5r 3j 4w 4j 5q 4k 5k 5y 6k
    3a 4q 4r 5q 4a 5j 5i 6a 4r 5q 5c 6n 5n 6k 6c 7c
    3a 4q 4r 5q 4a 5j 5i 6a 4r 5q 5c 6n 5n 6k 6c 7c
    3i 4n 4t 5r 4n 5e 5r 6i 4a 5a 5n 6a 5j 6e 6k 7e
    3i 4n 4t 5r 4n 5e 5r 6i 4a 5a 5n 6a 5j 6e 6k 7e
    4a 5j 5n 6k 5a 6e 6a 7e 5i 6a 6c 7c 6a 7e 7c 8
    4a 5j 5n 6k 5a 6e 6a 7e 5i 6a 6c 7c 6a 7e 7c 8
  `.trim().split(/\s+/);


/**
 * @typedef {import('./AbcSimpleBoard').TransitionFunction} TransitionFunction
 */ const $e7bed22a29553bb6$var$weightsByRelPos = new Map([
    [
        [
            -1,
            -1
        ],
        1
    ],
    [
        [
            -1,
            0
        ],
        2
    ],
    [
        [
            -1,
            1
        ],
        4
    ],
    [
        [
            0,
            -1
        ],
        8
    ],
    [
        [
            0,
            0
        ],
        16
    ],
    [
        [
            0,
            1
        ],
        32
    ],
    [
        [
            1,
            -1
        ],
        64
    ],
    [
        [
            1,
            0
        ],
        128
    ],
    [
        [
            1,
            1
        ],
        256
    ]
]);
/**
 * @private
 * @param {Array<Cell>} cellsArray
 * @returns {CellMap}
 */ const $e7bed22a29553bb6$var$getNeighborTransitionIndexes = (cellsArray)=>{
    const neighborTransitions = new (0, $b455d5ebeb71c1de$export$2e2bcd8739ae039)();
    cellsArray.forEach(([x, y])=>{
        $e7bed22a29553bb6$var$weightsByRelPos.forEach((w, [dx, dy])=>{
            const targetCell = [
                x - dx,
                y - dy
            ]; // So that [x, y] is at rel. pos. [dx, dy]
            const currentWeight = neighborTransitions.get(targetCell) || 0;
            neighborTransitions.set(targetCell, currentWeight + w);
        });
    });
    return neighborTransitions;
};
/**
 * @private
 * @param {INTRule} rule
 * @returns {TransitionFunction}
 */ const $e7bed22a29553bb6$var$makeTransitionFunction = (rule)=>(cellsArray)=>{
        const transitionIndexesByCell = $e7bed22a29553bb6$var$getNeighborTransitionIndexes(cellsArray);
        const nextCells = [
            ...transitionIndexesByCell.entries()
        ].filter(([, tIndex])=>{
            // The 0x10 bit is the center cell.
            // eslint-disable-next-line no-bitwise
            const cellIsAlive = (tIndex & 0x10) !== 0;
            const validTransitions = cellIsAlive ? rule.survivals : rule.births;
            return validTransitions.includes((0, $a97e00b3534022fc$export$10cb2c208909f427)[tIndex]);
        }).map(([cell])=>cell);
        return nextCells;
    };
class $e7bed22a29553bb6$var$SimpleINTBoard extends (0, $7c60ee827e62c391$export$2e2bcd8739ae039) {
    /**
   * @param {TwoStatePattern} pattern
   * @param {INTRule} rule
   * @param {number} gen
   */ constructor(pattern, rule, gen = 0){
        super(pattern, rule, gen);
        /** @type {TransitionFunction} */ this.transitionFunction = $e7bed22a29553bb6$var$makeTransitionFunction(rule);
    }
}
var $e7bed22a29553bb6$export$2e2bcd8739ae039 = $e7bed22a29553bb6$var$SimpleINTBoard;


const $3f27e148c19edda2$var$life = new (0, $6c574ea9d853ec19$export$2e2bcd8739ae039)([
    3
], [
    2,
    3
]);
const $3f27e148c19edda2$export$c68329e72259b1d3 = (pattern, rule = $3f27e148c19edda2$var$life)=>{
    if (rule.constructor === (0, $6c574ea9d853ec19$export$2e2bcd8739ae039)) return new (0, $cdbcd7a52f96e34a$export$2e2bcd8739ae039)(pattern, rule);
    if (rule.constructor === (0, $5fea755afec9d72f$export$2e2bcd8739ae039)) return new (0, $e7bed22a29553bb6$export$2e2bcd8739ae039)(pattern, rule);
    throw new Error("Invalid Rule!");
};
const $3f27e148c19edda2$export$127ea844e1508b8d = (rule = $3f27e148c19edda2$var$life)=>$3f27e148c19edda2$export$c68329e72259b1d3([], rule);


/**
 * @typedef {import('../IController').IController}  IController
 * @typedef {import('../../Models/AppState').default} AppState
 */ /**
 * A controller that updates the target with osc stats, with the given RLE.
 * @class
 * @implements {IController}
 */ class $ce1ca3b02054b188$var$OscStatsController {
    /**
   * @param {AppState} targetState
   * @param {HTMLInputElement} sourceElement
   */ constructor(targetState, sourceElement){
        /** @type {AppState} */ this.targetState = targetState;
        /** @type {HTMLInputElement} */ this.sourceElement = sourceElement;
        /**
     * The update callback.
     * @type {function(Event): void}
     */ this.update = (event)=>{
            if (!event.target) {
                this.targetState.setValue({
                    success: false,
                    message: "No event.target"
                });
                return;
            }
            const rleString = this.sourceElement.value;
            const { pattern: pattern , rule: rule  } = (0, $c0a50bf5651c6c70$export$98e6a39c04603d36)(rleString);
            if (!rule) {
                this.targetState.setValue({
                    success: false,
                    message: "Unable to parse rule"
                });
                return;
            }
            const initialBoard = $3f27e148c19edda2$export$c68329e72259b1d3(pattern, rule);
            this.targetState.setValue((0, $33c87e009b185634$export$a50fceef87b2948)(initialBoard));
        };
    }
}
var $ce1ca3b02054b188$export$2e2bcd8739ae039 = $ce1ca3b02054b188$var$OscStatsController;


/**
 * @typedef {import('../../../BaseTypes/BoundingBox').default} BoundingBox
 */ const $25ddeeb3e8fa7df7$var$colorscheme = {
    background: "#eeeeee",
    stator: "#000000",
    strictRotor: "#999999",
    liveCell: "#000000"
};
// Cell size follows `box-sizing: border-box`
// In other words, borders on all four sides are counted as cellSize.
const $25ddeeb3e8fa7df7$var$cellSizes = {
    cell: 10,
    border: 1,
    liveCell: 4,
    liveBorder: 2
};
/** @type {function(number, number): string} */ const $25ddeeb3e8fa7df7$var$makeGradientColor = (numColors, i)=>{
    const hue = Math.floor(360 * (i / numColors));
    return `hsl(${hue}, 100%, 70%)`;
};
const $25ddeeb3e8fa7df7$export$dafb4586fe1f0b47 = (period, subperiods)=>{
    // Remove special subperiods:
    //  subperiod 1 (stator)
    //  subperiod `period` (full-period rotor)
    const subperiodsSet = new Set(subperiods);
    subperiodsSet.delete(1);
    subperiodsSet.delete(period);
    const nonSpecialSubperiods = [
        ...subperiodsSet.values()
    ].sort((a, b)=>a - b);
    /** @type {Array<[number, string]>} */ const nonSpecialSubperiodsAndColors = nonSpecialSubperiods.map((sp, i)=>[
            sp,
            $25ddeeb3e8fa7df7$var$makeGradientColor(nonSpecialSubperiods.length, i)
        ]);
    return new Map([
        ...nonSpecialSubperiodsAndColors,
        [
            1,
            $25ddeeb3e8fa7df7$var$colorscheme.stator
        ],
        [
            period,
            $25ddeeb3e8fa7df7$var$colorscheme.strictRotor
        ]
    ]);
};
const $25ddeeb3e8fa7df7$export$20d2db9c68afeac = (canvas, context, boundingBox)=>{
    // We have to manipulate the canvas, so first beg pardon to ESLint
    /* eslint-disable no-param-reassign */ const { cell: cellSize , border: borderWidth  } = $25ddeeb3e8fa7df7$var$cellSizes;
    // First fill rect with empty cell backgrounds.
    const patternWidth = boundingBox.xmax - boundingBox.xmin + 1;
    const patternHeight = boundingBox.ymax - boundingBox.ymin + 1;
    // Add one cell for padding on each side.
    canvas.width = cellSize * (patternWidth + 2);
    canvas.height = cellSize * (patternHeight + 2);
    // Draw background
    context.fillStyle = $25ddeeb3e8fa7df7$var$colorscheme.background;
    context.fillRect(0, 0, canvas.width, canvas.height);
    // Draw cell borders
    context.fillStyle = "white";
    new Array(patternHeight + 2).fill().forEach((_, i)=>{
        context.fillRect(0, cellSize * i, canvas.width, borderWidth);
        context.fillRect(0, cellSize * (i + 1) - borderWidth, canvas.width, borderWidth);
    });
    new Array(patternWidth + 2).fill().forEach((_, i)=>{
        context.fillRect(cellSize * i, 0, canvas.height, borderWidth);
        context.fillRect(cellSize * (i + 1) - borderWidth, 0, borderWidth, canvas.height);
    });
};
const $25ddeeb3e8fa7df7$export$440d26a1852ff325 = (context, x, y, color)=>{
    context.fillStyle = color;
    // Displace cell by (+1, +1) to compensate for the 1-cell borders above.
    const cellSize = $25ddeeb3e8fa7df7$var$cellSizes.cell;
    const borderWidth = $25ddeeb3e8fa7df7$var$cellSizes.border;
    const rect = [
        (x + 1) * cellSize + borderWidth,
        (y + 1) * cellSize + borderWidth,
        cellSize - 2 * borderWidth,
        cellSize - 2 * borderWidth
    ];
    context.fillRect(...rect);
};
const $25ddeeb3e8fa7df7$export$a4f5dec905bc64b8 = (context, x, y)=>{
    context.fillStyle = $25ddeeb3e8fa7df7$var$colorscheme.liveCell;
    const { cell: cellSize , liveCell: liveCellSize  } = $25ddeeb3e8fa7df7$var$cellSizes;
    const rect = [
        // Draw at (x + 1, y + 1) for the same reason.
        // Padding at each side is half the difference of cell sizes.
        (x + 1) * cellSize + 0.5 * (cellSize - liveCellSize),
        (y + 1) * cellSize + 0.5 * (cellSize - liveCellSize),
        liveCellSize,
        liveCellSize
    ];
    context.fillRect(...rect);
};
const $25ddeeb3e8fa7df7$export$c90bea463c490a7a = (context, x, y)=>{
    context.fillStyle = $25ddeeb3e8fa7df7$var$colorscheme.liveCell;
    const { cell: cellSize , border: borderWidth , liveBorder: liveBorderWidth  } = $25ddeeb3e8fa7df7$var$cellSizes;
    // Draw at (x + 1, y + 1) for the same reason.
    const xLeft = (x + 1) * cellSize + borderWidth;
    const xRight = (x + 2) * cellSize - borderWidth - liveBorderWidth;
    const yTop = (y + 1) * cellSize + borderWidth;
    const yBottom = (y + 2) * cellSize - borderWidth - liveBorderWidth;
    const liveBorderLength = cellSize - 2 * borderWidth;
    context.fillRect(xLeft, yTop, liveBorderLength, liveBorderWidth);
    context.fillRect(xLeft, yBottom, liveBorderLength, liveBorderWidth);
    context.fillRect(xLeft, yTop, liveBorderWidth, liveBorderLength);
    context.fillRect(xRight, yTop, liveBorderWidth, liveBorderLength);
};


/**
 * @typedef {import('../../Models/AppState').default} AppState
 */ const $f8efe9e7a5a8ae37$var$drawLiveCellOptions = new Map([
    [
        "none",
        ()=>{}
    ],
    [
        "border",
        $25ddeeb3e8fa7df7$export$c90bea463c490a7a
    ],
    [
        "interior",
        $25ddeeb3e8fa7df7$export$a4f5dec905bc64b8
    ]
]);
/** @type {function(Array<{cell: Cell, subperiod: number}>): Array<number>} */ const $f8efe9e7a5a8ae37$var$getArrayFromSubperiods = (subperiods)=>{
    const subperiodSet = new Set(subperiods.map((e)=>e.subperiod));
    const subperiodArray = [
        ...subperiodSet.values()
    ].sort((a, b)=>a - b);
    return subperiodArray;
};
/**
 * The view that takes care of the oscillizer canvas.
 * @class
 * @implements {IView}
 */ class $f8efe9e7a5a8ae37$var$OscillizerCanvasView {
    /**
   * @param {*} oscData
   * @param {AppState} cellStyle
   * @param {HTMLCanvasElement} targetCanvas
   */ constructor(oscData, cellStyle, targetCanvas){
        this.oscData = oscData;
        this.cellStyle = cellStyle;
        this.targetCanvas = targetCanvas;
        /**
     * The `update` callback that updates the view.
     * @type {function(Event): void}
     */ this.update = ()=>{
            const context = this.targetCanvas.getContext("2d");
            if (!context) return;
            // Dependent data
            const { success: success , pattern: pattern , period: period , subperiods: subperiods , boundingBox: boundingBox  } = this.oscData.value;
            const colorMap = $25ddeeb3e8fa7df7$export$dafb4586fe1f0b47(period, $f8efe9e7a5a8ae37$var$getArrayFromSubperiods(subperiods));
            // Helper functions
            const drawSubperiod = ({ cell: [x, y] , subperiod: subperiod  })=>{
                $25ddeeb3e8fa7df7$export$440d26a1852ff325(context, x - boundingBox.xmin, y - boundingBox.ymin, colorMap.get(subperiod));
            };
            const drawLiveCell = ([x, y])=>{
                const drawLiveCellOption = $f8efe9e7a5a8ae37$var$drawLiveCellOptions.get(this.cellStyle.value || "none");
                drawLiveCellOption(context, x - boundingBox.xmin, y - boundingBox.ymin);
            };
            // Main Logic
            context.clearRect(0, 0, targetCanvas.width, targetCanvas.height);
            if (!success) return;
            $25ddeeb3e8fa7df7$export$20d2db9c68afeac(targetCanvas, context, boundingBox);
            subperiods.forEach(drawSubperiod);
            pattern.forEach(drawLiveCell);
        }; // end of `this.update`
    }
}
var $f8efe9e7a5a8ae37$export$2e2bcd8739ae039 = $f8efe9e7a5a8ae37$var$OscillizerCanvasView;


/** @typedef {import('../../Models/AppState').default} AppState */ /**
 * @class
 * @implements {IView}
 */ class $df4da37397fd5fdc$var$OscStatsView {
    /**
   * @param {AppState} sourceState
   * @param {HTMLElement} targetElement
   */ constructor(sourceState, targetElement){
        this.sourceState = sourceState;
        this.targetElement = targetElement;
        /** @type {function(Event): void} */ this.update = ()=>{
            const data = this.sourceState.value;
            if (!data.success) this.targetElement.innerText = `Failure: ${data.message}`;
            else this.targetElement.innerHTML = `
          <table>
            <thead>
              <tr>
                <th>Property</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr class="stripe-dark">
                <td>Period</td>
                <td>${data.period}</td>
              </tr>
              <tr class="stripe-dark">
                <td>Population</td>
                <td>Avg: ${data.avgPop}<br>Min: ${data.minPop}<br>Max: ${data.maxPop}</td>
              </tr>
              <tr class="stripe-dark">
                <td>Cells</td>
                <td>
                  Rotor: ${data.numRotorCells}<br>
                  Stator: ${data.numStatorCells}<br>
                  Total: ${data.numRotorCells + data.numStatorCells}
                </td>
              </tr>
              <tr class="stripe-dark">
                <td>Volatility</td>
                <td>${data.volatility}<br>(Strict: ${data.strictVolatility})</td>
              </tr>
              <tr class="stripe-dark">
                <td>Bounding Box</td>
                <td>
                  ${data.boundingBox.xmax - data.boundingBox.xmin + 1}
                  x ${data.boundingBox.ymax - data.boundingBox.ymin + 1}
                </td>
              </tr>
            </tbody>
          </table>
        `;
             // if ... else
        }; // this.update
    }
}
var $df4da37397fd5fdc$export$2e2bcd8739ae039 = $df4da37397fd5fdc$var$OscStatsView;


const $35da740d59af89ba$var$appState = {
    oscInfo: new (0, $06c1141a5fa11022$export$2e2bcd8739ae039)(),
    /*
    Cell style for cells at gen 0
    this.value: cellStyles.property
  */ initialCellStyle: new (0, $06c1141a5fa11022$export$2e2bcd8739ae039)()
};
// @ts-ignore
if (window.Cypress) // @ts-ignore
window.appState = $35da740d59af89ba$var$appState;
/* Action -> State update code */ const $35da740d59af89ba$var$inputRleSubmitter = document.getElementById("input-rle-submitter");
const $35da740d59af89ba$var$cellStyleSelector = document.getElementById("cell-style-selector");
const $35da740d59af89ba$var$inputRleContainer = document.getElementById("input-rle-container");
const $35da740d59af89ba$var$oscController = new (0, $ce1ca3b02054b188$export$2e2bcd8739ae039)($35da740d59af89ba$var$appState.oscInfo, $35da740d59af89ba$var$inputRleContainer);
const $35da740d59af89ba$var$cellStyleController = new (0, $005cdf1a540a6299$export$2e2bcd8739ae039)($35da740d59af89ba$var$appState.initialCellStyle);
$35da740d59af89ba$var$inputRleSubmitter.addEventListener("click", $35da740d59af89ba$var$oscController.update);
$35da740d59af89ba$var$cellStyleSelector.addEventListener("change", $35da740d59af89ba$var$cellStyleController.update);
/* State -> UI update code */ const $35da740d59af89ba$var$oscCanvas = /** @type {HTMLCanvasElement} */ document.getElementById("output-osc-canvas");
const $35da740d59af89ba$var$oscCanvasView = new (0, $f8efe9e7a5a8ae37$export$2e2bcd8739ae039)($35da740d59af89ba$var$appState.oscInfo, $35da740d59af89ba$var$appState.initialCellStyle, $35da740d59af89ba$var$oscCanvas);
$35da740d59af89ba$var$appState.oscInfo.eventTarget.addEventListener("change", $35da740d59af89ba$var$oscCanvasView.update);
const $35da740d59af89ba$var$oscStatsElement = document.getElementById("output-osc-data");
const $35da740d59af89ba$var$oscStatsView = new (0, $df4da37397fd5fdc$export$2e2bcd8739ae039)($35da740d59af89ba$var$appState.oscInfo, $35da740d59af89ba$var$oscStatsElement);
$35da740d59af89ba$var$appState.oscInfo.eventTarget.addEventListener("change", $35da740d59af89ba$var$oscStatsView.update);


//# sourceMappingURL=index.80f92531.js.map
